// Browser-side T(): the same contract as modules/language.py's T(), with the
// strings for the session language fetched once from /ui/lang/<code>.
//
// en-ca is the source language: its strings live inline in the code, so for
// en-ca there is nothing to fetch and T() is the identity. Missing translations
// fall back to the en-ca string, exactly as the server did.
//
// The legacy WB.* files call a global T() at runtime; index.html defines a stub
// that reads window.__wbStrings, which this module owns.
import { apiFetch } from './backend.js';

const DEFAULT_LANG = 'en-ca';
const CACHE_PREFIX = 'wb.lang.';

export const i18n = {
  code: DEFAULT_LANG,
  name: 'English (CA)',
  mcelocale: 'english',
  strings: {},
};

export function T(s) {
  if (s && s[0] === '!') {
    if (s === '!langcode!') return i18n.code;
    if (s === '!langname!') return i18n.name;
    if (s === '!langmcelocale!') return i18n.mcelocale;
    return s;
  }
  const v = i18n.strings[s];
  return v === undefined ? s : v;
}

function publish() {
  window.__wbStrings = i18n.strings;
  window.__wbLang = i18n;
  window.T = T;
}

export function setStrings(code, name, strings) {
  i18n.code = code || DEFAULT_LANG;
  i18n.name = name || i18n.name;
  i18n.strings = strings || {};
  publish();
}

// Fetch (or reuse from sessionStorage) the strings for `code`. Returns i18n.
export async function loadLanguage(code, { mcelocale } = {}) {
  code = code || DEFAULT_LANG;
  if (mcelocale) i18n.mcelocale = mcelocale;
  if (code === DEFAULT_LANG) {
    setStrings(code, 'English (CA)', {});
    return i18n;
  }
  let cached = null;
  try {
    cached = JSON.parse(sessionStorage.getItem(CACHE_PREFIX + code) || 'null');
  } catch (e) {
    cached = null;
  }
  if (cached && cached.strings) {
    setStrings(code, cached.name, cached.strings);
    // refresh in the background so a redeploy with new strings shows up next load
    fetchLanguage(code).catch(() => {});
    return i18n;
  }
  const data = await fetchLanguage(code);
  setStrings(code, data.name, data.strings);
  return i18n;
}

async function fetchLanguage(code) {
  const resp = await apiFetch('/ui/lang/' + encodeURIComponent(code));
  if (!resp.ok) throw new Error('ui/lang ' + resp.status);
  const data = await resp.json();
  try {
    sessionStorage.setItem(CACHE_PREFIX + code, JSON.stringify({ name: data.name, strings: data.strings }));
  } catch (e) {
    /* storage full or unavailable: strings still work for this page load */
  }
  return data;
}

publish();
